/* itemRatings
 * An LRU-style localStorage utility for storing and retrieving
 * item ratings (like/dislike) for a product card. It enforces a capacity limit
 * by purging the oldest entry when the limit is reached, ensuring that only
 * the most recent ratings are retained.
 */

import Cookies from 'js-cookie';
import { ReactionFeedback } from '../types/definitions/graphql-schema.d';

const BASE_STORAGE_KEY = 'itemRatingsCache';
const ATTENTIVE_CLIENT_ID_COOKIE = '__attentive_client_user_id';

// The maximum number of ratings to store before purging the oldest. We want a number that balances
// performance and saving the interactions a client might remember across sessions.
export const CAPACITY_LIMIT = 150;

/*
 * We retrieve the clientId from the __attentive_client_user_id cookie. This allows us to scope the
 * storage to the user without passing a prop, so that if they log out and another user logs in,
 * the ratings are not shared between users.
 */
const getStorageKey = (): string => {
  const clientId = Cookies.get(ATTENTIVE_CLIENT_ID_COOKIE);
  return clientId ? `${BASE_STORAGE_KEY}-${clientId}` : BASE_STORAGE_KEY;
};

export const storageKey = getStorageKey();

interface CacheStorage {
  map: Record<string, ReactionFeedback>;
  order: string[];
}

// Retrieve and parse localStorage data.
const getStorage = (): CacheStorage => {
  const stored = localStorage.getItem(storageKey);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to parse localStorage data:', error);
      // On parsing error, reinitialize the storage.
      return { map: {}, order: [] };
    }
  }
  return { map: {}, order: [] };
};

// Persist the storage object back to localStorage.
const setStorage = (storage: CacheStorage) => {
  try {
    localStorage.setItem(storageKey, JSON.stringify(storage));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to set localStorage data:', error);
  }
};

export const set = (skuId: string, rating: ReactionFeedback) => {
  const storage = getStorage();

  if (skuId in storage.map) {
    // Update the rating and refresh its recency:
    storage.map[skuId] = rating;
    // Remove skuId from the order array.
    storage.order = storage.order.filter(key => key !== skuId);
    // Reinsert at the end (most recent).
    storage.order.push(skuId);
  } else {
    // If we're at capacity, remove the oldest entry.
    if (storage.order.length >= CAPACITY_LIMIT) {
      const oldest = storage.order.shift();
      if (oldest) {
        delete storage.map[oldest];
      }
    }
    // Add the new record.
    storage.map[skuId] = rating;
    storage.order.push(skuId);
  }

  setStorage(storage);
};

export const get = (skuId?: string) => {
  const storage = getStorage();
  if (skuId) {
    return storage.map[skuId] || 'NEUTRAL';
  }
  return storage.map;
};

export default {
  set,
  get,
};
